import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API from 'app/api';
import modelAPI from 'app/apiModel';
import { setSnackbar } from './snackbarSlice';

// AR photos are a v0.4 resource (observation-service, presigned S3 flow — the
// same shape as arVideoSlice), so they go through the versionless modelAPI
// client; the main API client bakes in /v0.3. Reads return the v0.3 ARImage
// DTO unchanged, but the four *Url fields are presigned S3 GET URLs (60 min).
const constructUrl = (projectId, arImageId = '') =>
    `/v0.4/projects/${projectId}/arimages${arImageId ? `/${arImageId}` : ''}`;

// Only the legacy v0.3 route has a PUT (it rewrites the legacy URL columns);
// v0.4 has no update endpoint.
const legacyUrl = (projectId, arImageId) => `/project/${projectId}/aRImage/${arImageId}`;

// Uploads one PNG straight to S3 with plain axios: the presigned URL carries
// its own signature and S3 rejects a request that also has a Bearer header.
// Content-Type is signed into the URL, so it must match exactly (image/png).
const uploadToPresignedUrl = (file, { presignedUrl, contentType }) =>
    axios.put(presignedUrl, file, { headers: { 'Content-Type': contentType } });

// v0.4 upload is three steps: create (metadata → presigned PUT URLs), PUT both
// PNGs to S3, register (server checks the objects exist, builds the 512px
// thumbnails, activates the row). The image is hidden from every list until
// register succeeds.
export const createARImage = createAsyncThunk('arImage/create', async ({ projectId, worldFile, augmentedFile, metadata }, { dispatch }) => {
    try {
        const { data: ticket } = await modelAPI.post(constructUrl(projectId), metadata);
        const fileFor = (suffix) => ticket.files.find((f) => f.fileName.endsWith(suffix));
        await Promise.all([
            uploadToPresignedUrl(worldFile, fileFor('_world.png')),
            uploadToPresignedUrl(augmentedFile, fileFor('_augmented.png')),
        ]);
        const { data } = await modelAPI.post(`${constructUrl(projectId, ticket.id)}/register`);
        dispatch(setSnackbar({ status: 'success', message: 'AR Image created successfully' }));
        dispatch(getAllARImages({ projectId }));
        return data;
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: 'Failed to create AR Image' }));
        throw e;
    }
});

export const updateARImage = createAsyncThunk('arImage/update', async ({ projectId, id, arImage }, { dispatch }) => {
    const { data } = await API.put(legacyUrl(projectId, id), arImage);
    dispatch(setSnackbar({ status: 'success', message: 'AR Image updated successfully' }));
    dispatch(getAllARImages({ projectId }));
    return data;
});

export const getAllARImages = createAsyncThunk('arImage/getAll', async ({ projectId }, { dispatch }) => {
    try {
        const { data } = await modelAPI.get(constructUrl(projectId));
        return data;
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: 'Failed to fetch AR Images' }));
        throw e;
    }
});

export const getARImageById = createAsyncThunk('arImage/getById', async ({ projectId, id }, { dispatch }) => {
    try {
        const { data } = await modelAPI.get(constructUrl(projectId, id));
        dispatch(setSnackbar({ status: 'success', message: 'AR Image fetched successfully' }));
        return data;
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: 'Failed to fetch AR Image' }));
        throw e;
    }
});

export const deleteARImage = createAsyncThunk('arImage/delete', async ({ projectId, id }, { dispatch }) => {
    await modelAPI.delete(constructUrl(projectId, id));
    dispatch(setSnackbar({ status: 'success', message: 'AR Image deleted successfully' }));
    return id;
});

const arImageSlice = createSlice({
    name: 'arImage',
    initialState: {
        arImages: {},
        loading: false,
        error: null,
    },
    reducers: {
        clearARImages: () => arImageSlice.getInitialState(),
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllARImages.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(getAllARImages.fulfilled, (state, action) => {
                state.loading = false;
                state.arImages = action.payload.reduce((acc, img) => {
                    acc[img.Id ?? img.id] = img;
                    return acc;
                }, {});
            })
            .addCase(getAllARImages.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(createARImage.fulfilled, () => {})
            .addCase(updateARImage.fulfilled, () => {})
            .addCase(deleteARImage.fulfilled, (state, action) => {
                delete state.arImages[action.payload];
            });
    },
});

export const { clearARImages } = arImageSlice.actions;
export default arImageSlice.reducer;
